import { useState, useCallback } from "react";
import TiltCard from "@/components/TiltCard";
import { stripHtml, decodeEntities } from "@/lib/sanitize";
import {
  ListMusic, Plus, Trash2, Loader2, ImageIcon,
  ChevronDown, Play, Pause, ExternalLink, GripVertical, Settings, Eye, X,
} from "lucide-react";
import ShareEpisode from "@/components/ShareEpisode";
import { usePlaylists, type PlaylistItem } from "@/hooks/usePlaylists";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";
import AudioPlayer from "@/components/AudioPlayer";
import NoteButton from "@/components/NoteButton";
import PlaylistEditModal from "@/components/PlaylistEditModal";
import EmptyPlaylists from "@/components/empty-states/EmptyPlaylists";
import PlaylistDownloadButton from "@/components/PlaylistDownloadButton";

export default function PlaylistManager() {
  const {
    playlists, loading, generatingCovers, createPlaylist, deletePlaylist, updatePlaylist,
    fetchPlaylistItems, reorderItems, removeEpisode, refetch,
  } = usePlaylists();
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [items, setItems] = useState<PlaylistItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [editingPlaylist, setEditingPlaylist] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ type: "episode"; playlistId: string; episodeId: string } | null>(null);
  const [previewCover, setPreviewCover] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    const pl = await createPlaylist(newTitle.trim(), newDescription.trim() || undefined);
    if (pl) {
      toast.success(`Created "${pl.title}"`);
      setNewTitle("");
      setNewDescription("");
      setCreating(false);
    }
  };

  const toggleExpand = useCallback(async (playlistId: string) => {
    if (expandedId === playlistId) {
      setExpandedId(null);
      setItems([]);
      setPlayingAudio(null);
      return;
    }
    setExpandedId(playlistId);
    setItemsLoading(true);
    setPlayingAudio(null);
    const fetched = await fetchPlaylistItems(playlistId);
    setItems(fetched);
    setItemsLoading(false);
  }, [expandedId, fetchPlaylistItems]);

  const handleRemoveEpisode = async (playlistId: string, episodeId: string) => {
    setConfirmDelete({ type: "episode", playlistId, episodeId });
  };

  const executeDelete = async () => {
    if (!confirmDelete || confirmDelete.type !== "episode") return;
    await removeEpisode(confirmDelete.playlistId, confirmDelete.episodeId);
    setItems((prev) => prev.filter((i) => i.episode_id !== confirmDelete.episodeId));
    toast.success("Episode removed from playlist");
    setConfirmDelete(null);
  };

  const handleDragEnd = useCallback(async (result: DropResult) => {
    if (!result.destination || !expandedId) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;

    const reordered = Array.from(items);
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setItems(reordered);
    await reorderItems(expandedId, reordered);
  }, [items, expandedId, reorderItems]);

  const formatDuration = (s: number | null) => {
    if (!s) return "";
    return `${Math.floor(s / 60)} min`;
  };

  const truncate = (text: string | null, len = 120) => {
    if (!text) return "";
    return text.length > len ? text.slice(0, len) + "…" : text;
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 justify-center">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        <span className="font-mono text-xs text-muted-foreground">Loading playlists…</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {playlists.length === 0 && !creating && (
        <EmptyPlaylists
          onStartCreate={(preset) => {
            if (preset) {
              setNewTitle(preset.title);
              setNewDescription(preset.description ?? "");
            }
            setCreating(true);
          }}
        />
      )}

      {playlists.map((pl) => {
        const isExpanded = expandedId === pl.id;

        return (
          <TiltCard key={pl.id} className="retro-border retro-shadow-sm bg-card overflow-hidden hover-lift">
            {/* Playlist header row — clickable to expand */}
            <div
              className="p-3 sm:p-4 cursor-pointer"
              onClick={() => toggleExpand(pl.id)}
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0 flex items-start gap-3">
                  {/* Cover thumbnail — always visible */}
                  {pl.cover_image_url ? (
                    <div className="relative flex-shrink-0">
                      <img
                        src={pl.cover_image_url}
                        alt=""
                        className="w-24 h-24 sm:w-28 sm:h-28 rounded retro-border object-cover"
                      />
                      <button
                        onClick={(e) => { e.stopPropagation(); setPreviewCover(pl.cover_image_url); }}
                        className="absolute bottom-1 right-1 w-5 h-5 rounded-sm bg-foreground/60 hover:bg-foreground/80 text-background flex items-center justify-center transition-colors"
                        title="Preview cover"
                      >
                        <Eye className="w-3 h-3" />
                      </button>
                    </div>
                  ) : generatingCovers.has(pl.id) ? (
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded retro-border bg-muted flex items-center justify-center flex-shrink-0 animate-pulse">
                      <Loader2 className="w-5 h-5 text-muted-foreground/60 animate-spin" />
                    </div>
                  ) : null}
                  <div className="flex-1 min-w-0">
                    <span className="font-retro font-bold text-sm sm:text-base leading-tight hover:text-primary transition-colors text-left block">
                      {pl.title}
                    </span>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-[10px] sm:text-[11px] text-muted-foreground mt-1">
                      <span>{pl.item_count} episode{pl.item_count !== 1 ? "s" : ""}</span>
                      <span>{pl.is_public ? "Public" : "Private"}</span>
                    </div>
                    {pl.description && (
                      <p className="font-mono text-[10px] sm:text-[11px] text-muted-foreground/70 mt-1 line-clamp-2">
                        {pl.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Gear icon + download all */}
                <div className="flex flex-col items-end gap-2 flex-shrink-0 self-stretch">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingPlaylist(pl.id);
                    }}
                    className="p-1 text-muted-foreground/50 hover:text-foreground transition-colors"
                    title="Playlist settings"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                  {pl.item_count > 0 && (
                    <div className="mt-auto">
                      <PlaylistDownloadButton
                        playlistTitle={pl.title}
                        playlistId={pl.id}
                        items={isExpanded ? items : undefined}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Expanded episode list */}
            {isExpanded && (
              <div className="border-t-2 border-border">
                {itemsLoading && (
                  <div className="flex items-center gap-2 py-6 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    <span className="font-mono text-xs text-muted-foreground">Loading episodes…</span>
                  </div>
                )}

                {!itemsLoading && items.length === 0 && (
                  <div className="py-6 text-center">
                    <p className="font-mono text-xs text-muted-foreground">
                      No episodes yet — search and add some!
                    </p>
                  </div>
                )}

                {!itemsLoading && items.length > 0 && (
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId={`playlist-${pl.id}`}>
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="p-2 space-y-2">
                          {items.map((item, index) => {
                            const ep = item.episode;
                            const podcast = ep?.podcast;
                            const isPlaying = playingAudio === item.id;

                            return (
                              <Draggable key={item.id} draggableId={item.id} index={index}>
                                {(dragProvided, snapshot) => (
                                  <div
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                    className={`rounded-md border border-border bg-background ${
                                      snapshot.isDragging ? "shadow-lg ring-2 ring-primary/20" : ""
                                    }`}
                                  >
                                    {/* Top row: drag handle + artwork + info + play button */}
                                    <div className="px-3 py-2.5 flex items-center gap-2">
                                      <div
                                        {...dragProvided.dragHandleProps}
                                        className="text-muted-foreground/40 hover:text-muted-foreground cursor-grab active:cursor-grabbing flex-shrink-0"
                                      >
                                        <GripVertical className="w-3.5 h-3.5" />
                                      </div>

                                      {podcast?.artwork_url ? (
                                        <img
                                          src={podcast.artwork_url}
                                          alt=""
                                          className="w-10 h-10 sm:w-12 sm:h-12 retro-border object-cover flex-shrink-0"
                                        />
                                      ) : (
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 retro-border bg-muted flex items-center justify-center flex-shrink-0">
                                          <span className="font-retro text-sm text-muted-foreground">
                                            {podcast?.name?.charAt(0) || "?"}
                                          </span>
                                        </div>
                                      )}

                                      <div className="flex-1 min-w-0">
                                        <h4 className="font-retro font-bold text-xs sm:text-sm leading-tight mb-0.5 truncate">
                                          {ep?.slug ? (
                                            <Link to={`/episode/${ep.slug}`} className="hover:text-primary transition-colors hover:underline">
                                              {decodeEntities(ep.title)}
                                            </Link>
                                          ) : (
                                            decodeEntities(ep?.title || "Unknown episode")
                                          )}
                                        </h4>
                                        <p className="font-mono text-[10px] sm:text-[11px] text-muted-foreground">
                                          {podcast?.name || "Unknown podcast"}
                                          {ep?.published_at && (
                                            <> · {new Date(ep.published_at).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</>
                                          )}
                                          {ep?.duration_seconds && <> · {formatDuration(ep.duration_seconds)}</>}
                                        </p>
                                      </div>

                                      {ep?.audio_url && (
                                        <button
                                          onClick={() => setPlayingAudio(isPlaying ? null : item.id)}
                                          className={`comic-play-btn relative overflow-hidden flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 border-[3px] border-[hsl(195,80%,28%)] rounded-md bg-[hsl(195,85%,55%)] text-white flex items-center justify-center transition-all duration-150 transform shadow-[2px_2px_0px_0px_hsl(var(--foreground))] hover:shadow-[1px_1px_0px_0px_hsl(var(--foreground))] hover:translate-x-[1px] hover:translate-y-[1px] hover:scale-110 active:scale-90 ${
                                            isPlaying ? "rotate-0" : "rotate-[-2deg] hover:rotate-0"
                                          }`}
                                          title={isPlaying ? "Close player" : "Play"}
                                        >
                                          {isPlaying ? <Pause className="w-4 h-4 relative z-10" strokeWidth={3} /> : <Play className="w-4 h-4 ml-0.5 relative z-10" strokeWidth={3} fill="currentColor" />}
                                        </button>
                                      )}
                                    </div>

                                    {/* Action bar — tightly coupled with muted bg */}
                                    <div className="flex items-center justify-around bg-muted/40 rounded-b-md px-2 py-1">
                                      <NoteButton
                                        episodeId={ep?.id || item.episode_id}
                                        episodeTitle={ep?.title || "Episode"}
                                        audioUrl={ep?.audio_url}
                                        durationSeconds={ep?.duration_seconds}
                                        podcastName={podcast?.name}
                                        artworkUrl={podcast?.artwork_url}
                                      />
                                      {ep?.slug && (
                                        <Link
                                          to={`/episode/${ep.slug}`}
                                          className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                                          title="View episode"
                                        >
                                          <ExternalLink className="w-3.5 h-3.5" />
                                        </Link>
                                      )}
                                      <ShareEpisode
                                        episodeSlug={ep?.slug || item.episode_id}
                                        episodeTitle={ep?.title || "Episode"}
                                        podcastName={podcast?.name}
                                      />
                                      <button
                                        onClick={() => handleRemoveEpisode(pl.id, item.episode_id)}
                                        className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"
                                        title="Remove from playlist"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>

                                    {/* Inline audio player */}
                                    {isPlaying && ep?.audio_url && (
                                      <AudioPlayer
                                        src={ep.audio_url}
                                        title={ep.title}
                                        podcastName={podcast?.name}
                                        artworkUrl={podcast?.artwork_url || undefined}
                                        episodeId={ep.id}
                                        playlistId={pl.id}
                                        autoPlay
                                      />
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}

                {!itemsLoading && items.length > 0 && (
                  <div className="flex justify-end px-3 pb-3 pt-1">
                    <PlaylistDownloadButton playlistTitle={pl.title} items={items} />
                  </div>
                )}
              </div>
            )}
          </TiltCard>
        );
      })}

      <div className="pt-1">
        {creating ? (
          <div className="retro-border bg-card p-3 sm:p-4 space-y-2 rounded-md">
              <input
                autoFocus
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                placeholder="Playlist name…"
                className="input-retro"
              />
              <textarea
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Description (optional)…"
                rows={2}
                className="textarea-retro min-h-[60px]"
              />
              <div className="flex gap-2">
              <button
                onClick={handleCreate}
                disabled={!newTitle.trim()}
                className="btn-comic-primary btn-comic-sm disabled:opacity-50"
              >
                Create
              </button>
              <button
                onClick={() => { setCreating(false); setNewTitle(""); setNewDescription(""); }}
                className="btn-comic-ghost btn-comic-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : playlists.length > 0 ? (
          <button
            onClick={() => setCreating(true)}
            className="btn-comic-primary w-full"
          >
            <Plus className="w-4 h-4" /> New Playlist
          </button>
        ) : null}
      </div>

      {editingPlaylist && (() => {
        const pl = playlists.find((p) => p.id === editingPlaylist);
        if (!pl) return null;
        return (
          <PlaylistEditModal
            open
            onClose={() => setEditingPlaylist(null)}
            playlistId={pl.id}
            currentTitle={pl.title}
            currentDescription={pl.description}
            currentCoverUrl={pl.cover_image_url}
            coverGenerationCount={pl.cover_generation_count || 0}
            isPublic={pl.is_public}
            slug={pl.slug}
            onCoverUpdated={() => refetch()}
            onVisibilityChanged={() => refetch()}
            onDelete={async () => {
              await deletePlaylist(pl.id);
              if (expandedId === pl.id) {
                setExpandedId(null);
                setItems([]);
              }
              toast.success("Playlist deleted");
            }}
          />
        );
      })()}

      {/* Episode remove confirmation dialog */}
      {confirmDelete && confirmDelete.type === "episode" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-foreground/50 backdrop-blur-sm animate-fade-in" onClick={() => setConfirmDelete(null)} />
          <div className="relative w-full max-w-xs modal-shell animate-scale-in p-5">
            <h3 className="font-mono font-bold text-base mb-2">Remove Episode?</h3>
            <p className="font-sans text-sm text-muted-foreground mb-4">
              This will remove the episode from this playlist.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmDelete(null)}
                className="btn-comic-ghost btn-comic-sm"
              >
                Cancel
              </button>
              <button
                onClick={executeDelete}
                className="btn-comic-danger btn-comic-sm"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cover preview modal */}
      {previewCover && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={() => setPreviewCover(null)}>
          <div className="absolute inset-0 bg-foreground/70 backdrop-blur-sm animate-fade-in" />
          <div className="relative animate-scale-in max-w-lg w-full">
            <button
              onClick={() => setPreviewCover(null)}
              className="absolute -top-3 -right-3 z-10 w-8 h-8 rounded-full bg-card retro-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <img src={previewCover} alt="Playlist cover" className="w-full rounded retro-border retro-shadow object-contain" />
          </div>
        </div>
      )}
    </div>
  );
}